// apps/nos/client/src/hooks/useSentryUser.ts
import { useEffect } from "react";
import { useAuth, useOrganization, useUser } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { sentryEnabled } from "@/lib/sentryInit";

/**
 * Syncs the current Clerk user into Sentry's scope so every event
 * fired from the client carries identity. Mounted once inside
 * ProtectedShell — runs on sign-in, on user/org change, and on sign-out.
 */
export function useSentryUser(): void {
  const { isLoaded: authLoaded, isSignedIn, userId } = useAuth();
  const { user } = useUser();
  const { organization, membership } = useOrganization();

  useEffect(() => {
    if (!sentryEnabled || !authLoaded) return;

    if (!isSignedIn) {
      Sentry.setUser(null);
      return;
    }

    const email = user?.primaryEmailAddress?.emailAddress;
    if (email) {
      Sentry.setUser({ id: userId ?? "<unknown>", email });
    } else {
      Sentry.setUser({ id: userId ?? "<unknown>" });
    }
    Sentry.setTag("email", email ?? "<none>");
    Sentry.setTag("clerk_user_id", userId ?? "<unknown>");
    if (organization?.id) Sentry.setTag("clerk_org_id", organization.id);
    if (membership?.id) Sentry.setTag("clerk_membership_id", membership.id);
  }, [authLoaded, isSignedIn, userId, user, organization, membership]);
}
